/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */


import { useContext, useRef } from 'react';

import { Components, isTopModelSku, PanelItem, type Size, Vertex2 } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { APPLIANCE_FONT_SIZE, DIMENSION_FONT_SIZE } from '@r-core/constants';
import { DrawPanelItemDimensions } from '../draw-dimensions/draw-panel-item-dimensions';
import { getPanelItemVectors } from '@r-core/helpers';
import { Layer } from '@r-core/types';
import { NON_APPLIANCE_CATEGORY_KEYS } from '../../context-menu/helpers/is-swappable-appliance';
import { renderTracking } from '@r-services/render-tracking';
import { svgPolygonFromVectors } from '../helpers/svg-polygon-from-vectors';
import { useDraggableSvg } from '@r-services/ui-interactions';
import { usePanelItem } from '@r-hooks';

import { DesignerOptions, DisplayMode } from '@r-core';

export type RenderOverheadPanelItemProps = {
	panelItem: PanelItem;
	designerOptions: DesignerOptions;
};

export const RenderOverheadPanelItem = ({ panelItem }: RenderOverheadPanelItemProps): React.ReactElement => {
	const {
		controller: { options, navigation }
	} = useContext(AppContext);

	const movableRef = useRef(null);
	const uPanelItem = usePanelItem(panelItem, { isCountertop: true, options, displayMode: navigation.displayMode });

	const itemVectors = getPanelItemVectors({ panelItem, isCountertop: true });
	const overlayVectors = itemVectors.filter((vector) => vector.layer === Layer.Overlay);
	// const cutoutVectors = itemVectors.filter((vector) => vector.layer === Layer.Panel);

	const isCustomTopOnly = (panelItem.item.sku === 'CUSTOM' || panelItem.item.sku === 'CUTOUT') && panelItem.item.placement === Components.PLACEMENT_TOP_ONLY;
	const hasTopOnlySvg = panelItem.item.placement === Components.PLACEMENT_TOP_ONLY && !!panelItem.item.component.svg;
	const isSkuMode = navigation.displayMode === DisplayMode.SkuOverhead || navigation.displayMode === DisplayMode.SkuOverheadManufacturing;
	// In SKU mode, only render the visual when the item opts in via 'image' mode (or CUSTOM placeholders).
	// In non-SKU overhead modes, TOP_ONLY-with-svg items keep their classic "always visible" SVG behavior.
	const renderImageInSkuMode = panelItem.item.showInSkuOverhead && panelItem.item.skuOverheadMode === 'image';
	const isCutoutItem = (panelItem.item.category?.key === 'cutout' || panelItem.item.category?.key === 'hole')
		&& panelItem.item.placement === Components.PLACEMENT_TOP_ONLY;
	const isAlwaysVisibleOnCountertop = isSkuMode
		? renderImageInSkuMode || isCustomTopOnly || (isCutoutItem && !panelItem.item.showInSkuOverhead)
		: hasTopOnlySvg || isCustomTopOnly;

	// Plain (catalog) cutouts/openings always keep their internal dimensions in the SKU
	// overhead hybrid view, even though their visual cutout/appliance opacity is hidden there.
	const isPlainCutoutCategory = navigation.displayMode === DisplayMode.SkuOverhead
		&& NON_APPLIANCE_CATEGORY_KEYS.has(panelItem.item.category?.key ?? '')
		&& panelItem.item.sku !== 'CUSTOM'
		&& !panelItem.item.override;

	let applianceOpacity = 1;
	let cutoutOpacity = 1;
	let { showAppliances, showCutouts } = options;
	switch (navigation.displayMode) {
		case DisplayMode.Manufacturing:
		case DisplayMode.OverheadManufacturing:
			showAppliances = false;
			showCutouts = true;
			break;
		case DisplayMode.SkuOverhead:
		case DisplayMode.SkuOverheadManufacturing:
			showAppliances = false;
			showCutouts = false;
			break;
		default:
			// max 75%, we dont have images so let some of the cutout show through
			applianceOpacity = (100 - options.applianceOpacity) / 100;
			cutoutOpacity = options.showAppliances ? 0.3 : (100 - options.cutoutOpacity) / 100;
	}

	const applianceFontSize = APPLIANCE_FONT_SIZE * options.appliance.fontSizeFactor;
	const yLabel = uPanelItem._label.origin.z;
	const xLabel: number = uPanelItem._label.origin.x;
	const alignLabel = 'end';
	const label = panelItem.item.displayText;

	useDraggableSvg({
		ref: movableRef,
		enabled: [false, panelItem.item.placement === Components.PLACEMENT_TOP_ONLY],
		cursor: ['inherit', 'move'],
		position: { x: panelItem.center.x, y: panelItem.center.z },
		bBox: {
			origin: new Vertex2({ dataIn: { x: 0, y: 0 }}),
			size: {
				width: 0,
				height: (panelItem.refMap.island?.size as Size).depth ?? 0,
				depth: 0
			},
			padding: [0, panelItem.size.depth / 2, 0, panelItem.size.depth / 2]
		},
		onDragging: (e) => {
			const {
				position: { new: newPosition }
			} = e;

			const pt = new Vertex2({ dataIn: newPosition });
			pt.rotateAxis(new Vertex2({ dataIn: { x: 0, y: 0 }}), panelItem.refMap.island.axis.y);
			panelItem.move({ x: 0, z: pt.y });
			return true;
		}
	});

	// const draggableOptions: UseDraggableOptions = {
	// 	svgElement: movableRef,
	// 	component: panelItem.center,
	// 	ref: panelItem.id,
	// 	movableAxis: {
	// 		z: panelItem.item.placement === Components.PLACEMENT_TOP_ONLY
	// 	}
	// };

	// const { events } = useDraggableDepricated(draggableOptions);
	// Instance rotation for overhead view: Z rotation spins on the countertop
	const { z: irZ } = panelItem.effectiveRotation;
	const overheadRotationDeg = -irZ * (180 / Math.PI);

	renderTracking.incrementRender('RenderOverheadPanelItem');

	if (!panelItem.item.showInOverhead) return <></>;

	const isSpacer = panelItem.item.category?.isSpacer ?? false;
	const isManufacturingView = navigation.displayMode === DisplayMode.Manufacturing ||
		navigation.displayMode === DisplayMode.OverheadManufacturing ||
		navigation.displayMode === DisplayMode.SkuOverheadManufacturing;

	// Spacers do not render in manufacturing views
	if (isSpacer && isManufacturingView) return <></>;

	// Spacer items render a dashed outline + label instead of filled cutout
	if (isSpacer) return (
		<>
			<g
				ref={movableRef}
				overflow="visible"
				transform={`translate(${panelItem.center.x}, ${panelItem.center.z})`}
				type="panel-item-spacer"
			>
				<rect
					x={-panelItem.size.width / 2}
					y={-panelItem.size.depth / 2}
					width={panelItem.size.width}
					height={panelItem.size.depth}
					fill="lightgray"
					fillOpacity={0.5}
					stroke="darkblue"
					strokeWidth="0.15"
					strokeDasharray=".5"
					opacity={cutoutOpacity}
					pointerEvents="all"
				/>
				</g>
			{(showCutouts) && <DrawPanelItemDimensions panelItemKey={panelItem.id} isCountertop={true} />}
		</>
	);
	return (
		<>
			<g
				ref={movableRef}
				overflow="visible"
				transform={`translate(${panelItem.center.x}, ${panelItem.center.z})${overheadRotationDeg !== 0 ? ` rotate(${overheadRotationDeg})` : ''}`}
				type="panel-item-appliance"
					// {...events}
			>
				{/* add selection box for draggable top-only items - rendered last to be on top */}
				{panelItem.item.placement === Components.PLACEMENT_FRONT_AND_TOP ? (
					<>
						{/** Appliance Overlay **/}
						{showAppliances &&
							panelItem.sleeve === null &&
							overlayVectors.length > 0 &&
							svgPolygonFromVectors(overlayVectors, { fill: 'grey', opacity: applianceOpacity })}
						{/**  Cutout (hidden in SKU mode) **/}
						{!isSkuMode && (showCutouts && panelItem.sleeve === null && panelItem.isStandAlone
							? svgPolygonFromVectors(
									itemVectors.filter((vector) => vector.layer === Layer.Overlay),
									{ fill: 'white', opacity: cutoutOpacity }
							)
							: svgPolygonFromVectors(
									itemVectors.filter((vector) => vector.layer === Layer.Panel),
									{ fill: 'white', opacity: cutoutOpacity }
							))}
						{/* Draw Component Label - only if item.showLabel is true (hidden in SKU mode for overhead) */}
						{!isSkuMode && panelItem.item.showLabel && panelItem.sleeve === null && (
							<text textAnchor={alignLabel} x={xLabel} y={yLabel} fontSize={applianceFontSize} className="rta-designer-component-label" opacity={cutoutOpacity}>
								{label}
							</text>
						)}
								</>
				) : panelItem.item.placement === Components.PLACEMENT_TOP_ONLY ? (
					<>
						{/** Appliance Overlay - skip when SVG handles the visual **/}
						{showAppliances &&
							!panelItem.item.component.svg &&
							panelItem.sleeve === null &&
							overlayVectors.length > 0 &&
							svgPolygonFromVectors(overlayVectors, { fill: 'grey', opacity: applianceOpacity })}
						{/**  Cutout - only draw for items that cut into the countertop **/}
						{(showCutouts || isAlwaysVisibleOnCountertop) && (panelItem.item.distanceBelowCountertop ?? 0) > 0 && (
							panelItem.item.component.svg?.cutout({ fill: options.backgroundColor, opacity: cutoutOpacity, diameter: panelItem.item.cutout.width })
							?? (panelItem.sleeve === null && panelItem.isStandAlone
								? svgPolygonFromVectors(
										itemVectors.filter((vector) => vector.layer === Layer.Overlay),
										{ fill: options.backgroundColor, opacity: cutoutOpacity }
								)
								: svgPolygonFromVectors(
										itemVectors.filter((vector) => vector.layer === Layer.Panel),
										{ fill: 'white', opacity: cutoutOpacity }
								))
						)}
						{/* Draw Component Label (hidden in SKU mode; always-visible items never show label) */}
						{!isSkuMode && !isAlwaysVisibleOnCountertop && panelItem.item.showLabel && panelItem.sleeve === null && (
							<text textAnchor={alignLabel} x={xLabel} y={yLabel} fontSize={applianceFontSize} className="rta-designer-component-label" opacity={cutoutOpacity}>
								{label}
							</text>
						)}
					</>
				) : (
					<>
					</>
				)}
				{/* svg cutout outline (SKU mode: always-visible items only). offPanelDetail SKUs (#282/#385) render bare geometry here -- their callouts appear only on the countertop sheet, never on the overhead family. */}
				{(!isSkuMode || isAlwaysVisibleOnCountertop) && !(isTopModelSku(panelItem.item.sku) && !isManufacturingView) && !panelItem.item.component.svg?.offPanelDetail && panelItem.item.component.svg?.item?.({ diameter: panelItem.item.cutout.width, fontSize: DIMENSION_FONT_SIZE * options.dimensions.fontSizeFactor })}
				{/* Selection box for draggable top-only items - rendered last to be on top */}
				{panelItem.item.placement === Components.PLACEMENT_TOP_ONLY && <rect
					x={-panelItem.size.width / 2}
					y={-panelItem.size.depth / 2}
					width={panelItem.size.width}
					height={panelItem.size.depth}
					fill="white"
					stroke="none"
					opacity={0}
					pointerEvents="all"
				/>}
			</g>
			{/* PanelItem dimensions — wrapped in rotation transform so they follow the appliance */}
			<g transform={overheadRotationDeg !== 0 ? `translate(${panelItem.center.x}, ${panelItem.center.z}) rotate(${overheadRotationDeg}) translate(${-panelItem.center.x}, ${-panelItem.center.z})` : undefined}>
				{panelItem.item.placement === Components.PLACEMENT_FRONT_AND_TOP
					? ((!isSkuMode || isPlainCutoutCategory) && <DrawPanelItemDimensions panelItemKey={panelItem.id} isCountertop={true} />)
					: panelItem.item.placement === Components.PLACEMENT_TOP_ONLY
						? ((showCutouts || isAlwaysVisibleOnCountertop || isPlainCutoutCategory) && <DrawPanelItemDimensions panelItemKey={panelItem.id} isCountertop={true} />)
						: null
				}
			</g>
			{options.showDeveloperInsights && (
				<>
					<circle cx={panelItem.center.x} cy={panelItem.center.z} r=".25" fill="darkgreen" />

					<circle cx={uPanelItem._cutout.corners.top[0].x} cy={uPanelItem._cutout.corners.top[0].y} r=".35" fill="yellow" />
					<circle cx={uPanelItem._cutout.origin.x} cy={uPanelItem._cutout.origin.z} r=".35" fill="yellow" />
					<rect
						x={uPanelItem._cutout.corners.top[3].x}
						y={uPanelItem._cutout.corners.top[0].y}
						height={panelItem.item.cutout.depth}
						width={panelItem.item.cutout.width}
						opacity={cutoutOpacity}
						strokeWidth="0.1875"
						stroke="yellow"
						fill="none"
					/>

					<circle cx={uPanelItem._item.corners.top[0].x} cy={uPanelItem._item.corners.top[0].y} r=".25" fill="blue" />
					<circle cx={uPanelItem._item.origin.x} cy={uPanelItem._item.origin.z} r=".25" fill="blue" />
					<rect
						x={uPanelItem._item.corners.top[3].x}
						y={uPanelItem._item.corners.top[0].y}
						height={panelItem.item.size.depth}
						width={panelItem.item.size.width}
						opacity={cutoutOpacity}
						strokeWidth="0.125"
						stroke="blue"
						fill="none"
					/>

					<circle cx={uPanelItem._label.origin.x} cy={uPanelItem._label.origin.z} r=".25" fill="purple" />
				</>
			)}
		</>
	);
};
